/**
 * PURPOSE: Consumir el módulo Hayllis V2 de Match Officials desde Winscore Web.
 * RESPONSIBILITIES: Catálogo de roles, Master de oficiales (rol 1), asignación por partido y
 *   Designaciones por fecha, incluyendo la descarga del PDF.
 * COLLABORATORS: CompanyOfficialsTab.jsx, ModalMatchOfficials.jsx, DesignacionesTab.jsx.
 * ROUTE: winscore/src/services/matchOfficialsService.js
 */
import axios from "axios";
import httpService from "./httpService.js";
import API_BASE_URL from "../config/config.js";

const BASE_PATH = "/api/hayllis/v2/match-officials";

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const toArray = (value) => (Array.isArray(value) ? value : []);

// error?.message is checked last (before the caller's fallback) so a locally-thrown Error (e.g.
// the PDF response-validation errors below) still surfaces its own specific text instead of always
// falling back to the caller's generic message.
export const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.msg || error?.message || fallback;

const decodeArrayBufferJson = (arrayBuffer) => {
  try {
    return JSON.parse(new TextDecoder("utf-8").decode(arrayBuffer));
  } catch {
    return null;
  }
};

export const listRolesForSport = async (idsport) => {
  const response = await httpService.get(`${BASE_PATH}/roles`, { params: { idsport } });
  return unwrap(response);
};

// FTR-0004: also exposes the additive `sportFamilies:[{key,name}]` list from the same response —
// the 4-checkbox source for Officials Master eligibility, with no second HTTP request for it.
export const listRoleCatalog = async () => {
  const response = await httpService.get(`${BASE_PATH}/roles/catalog`);
  const payload = unwrap(response);
  return { families: toArray(payload?.families), sportFamilies: toArray(payload?.sportFamilies) };
};

export const listCompanyOfficials = async (idcompany, includeInactive = true) => {
  const response = await httpService.get(`${BASE_PATH}/companies/${idcompany}/officials`, {
    params: { includeInactive },
  });
  return toArray(unwrap(response)?.officials);
};

export const createOfficial = async (payload) => {
  const response = await httpService.post(`${BASE_PATH}/officials`, payload);
  return unwrap(response)?.official;
};

export const updateOfficial = async (idofficial, payload) => {
  const response = await httpService.patch(`${BASE_PATH}/officials/${idofficial}`, payload);
  return unwrap(response)?.official;
};

export const setOfficialState = async (idofficial, active) => {
  const response = await httpService.patch(`${BASE_PATH}/officials/${idofficial}/state`, { active });
  return unwrap(response)?.official;
};

export const replaceOfficialSportEligibility = async (idofficial, sportFamilyKeys) => {
  const response = await httpService.put(`${BASE_PATH}/officials/${idofficial}/eligibility`, {
    sportFamilyKeys,
  });
  return unwrap(response)?.official;
};

export const getMatchOfficialsDesignation = async (idmatch) => {
  const response = await httpService.get(`${BASE_PATH}/matches/${idmatch}/designation`);
  return unwrap(response)?.designation;
};

export const saveMatchOfficialsAssignments = async (idmatch, assignments) => {
  const response = await httpService.put(`${BASE_PATH}/matches/${idmatch}/assignments`, { assignments });
  return unwrap(response)?.designation;
};

export const setMatchOfficialsStatus = async (idmatch, status) => {
  const response = await httpService.patch(`${BASE_PATH}/matches/${idmatch}/status`, { status });
  return unwrap(response)?.designation;
};

// Match-context quick-create/enable-and-assign (runtime finding 2): read-only preview, never
// mutates. Backend re-resolves the same outcome independently at confirm time.
export const lookupOfficialForQuickCreate = async (idmatch, idrole, documentNumber) => {
  const response = await httpService.get(
    `${BASE_PATH}/matches/${idmatch}/roles/${idrole}/officials/lookup`,
    { params: { documentNumber } }
  );
  return unwrap(response);
};

export const quickCreateAndAssignOfficial = async (idmatch, idrole, payload) => {
  const response = await httpService.post(
    `${BASE_PATH}/matches/${idmatch}/roles/${idrole}/officials/quick-create`,
    payload
  );
  return unwrap(response);
};

// Real scheduling phase/jornada identifiers (idtypephase, nroFecha) — the same ones Programación
// itself filters by (runtime finding 1) — never a raw calendar date.
export const listDesignationsByDate = async ({ idevent, idsport, idtypephase, nroFecha }) => {
  const response = await httpService.get(`${BASE_PATH}/designations`, {
    params: { idevent, idsport, idtypephase, nroFecha },
  });
  return unwrap(response);
};

export const downloadDesignationsPdf = async ({ idevent, idsport, idtypephase, nroFecha, phaseLabel }) => {
  let response;
  try {
    response = await axios.get(`${API_BASE_URL}${BASE_PATH}/designations/pdf`, {
      params: { idevent, idsport, idtypephase, nroFecha, phaseLabel },
      responseType: "arraybuffer",
      withCredentials: true,
    });
  } catch (error) {
    // Non-2xx: axios already rejected before we ever touch the (possibly JSON) body. Since
    // responseType is arraybuffer, error.response.data is raw bytes even for a JSON error body —
    // decode it so the user sees the real backend message instead of a generic one.
    const apiPayload =
      error?.response?.data instanceof ArrayBuffer ? decodeArrayBufferJson(error.response.data) : null;
    const apiMessage = apiPayload?.message || apiPayload?.msg || null;
    console.error("[Designaciones PDF] HTTP/API error", {
      status: error?.response?.status,
      contentType: error?.response?.headers?.["content-type"],
      apiMessage,
    });
    throw new Error(apiMessage || "No se pudo generar el PDF de designaciones.");
  }

  const { data, headers } = response;
  const contentType = headers["content-type"] ?? "";

  if (!contentType.startsWith("application/pdf")) {
    console.error("[Designaciones PDF] Unexpected Content-Type on a 2xx response", { contentType });
    throw new Error("El servidor no devolvió un PDF válido (tipo de contenido inesperado).");
  }

  // Belt-and-suspenders: a 2xx + correct Content-Type is not, by itself, proof the bytes are a
  // real PDF — verify the %PDF- signature before ever handing this to the browser as a download.
  const signature = String.fromCharCode(...new Uint8Array(data.slice(0, 5)));
  if (signature !== "%PDF-") {
    console.error("[Designaciones PDF] Malformed binary — missing %PDF- signature", {
      signature,
      byteLength: data.byteLength,
    });
    throw new Error("El PDF generado no es válido. Intenta nuevamente.");
  }

  const disposition = headers["content-disposition"];
  let filename = "designaciones.pdf";
  const match = disposition && disposition.match(/filename="?([^";]+)"?/);
  if (match) filename = match[1];

  const blob = new Blob([data], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(blobUrl);
};
